import React, { useState, useCallback, ChangeEvent } from 'react';
import { debounce } from 'underscore';
import { OrderedMap } from 'immutable';

import { search } from './research_interest_search';
import { ResearchInterest } from './types';
import { ResearchInterestTags } from './ResearchInterestTags';

type SearchFunction = (
  query: string,
  risToExclude: Array<number>,
) => Promise<Array<ResearchInterest>>;

interface Props {
  maxResearchInterests: number;
  selectedResearchInterests: Array<ResearchInterest>;
  onAdd(ri: ResearchInterest): void;
  onRemove(ri: ResearchInterest): void;
  requiredText?: string;
  disabled?: boolean;
  hasError?: boolean;

  // Optional, used to provide a stub implementation for testing/storybook
  searchResearchInterests?: SearchFunction;
}

export function ResearchInterestTagsInput(props: Props) {
  const {
    searchResearchInterests,
    onAdd,
    onRemove,
    selectedResearchInterests,
    maxResearchInterests,
    requiredText,
    disabled = false,
    hasError = false,
  } = props;

  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<ResearchInterest>>(
    [],
  );

  // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    createDebouncedSearch(searchResearchInterests ?? search),
    [],
  );

  const onSearchComplete = (results: Array<ResearchInterest>) => {
    setSearchResults(results);
    setIsLoading(false);
  };

  const onQueryInput = (e: ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    if (newQuery) {
      setIsLoading(true);
      const risToExclude = selectedResearchInterests.map((ri) => ri.id);
      debouncedSearch(newQuery, risToExclude, onSearchComplete);
    } else {
      setIsLoading(false);
      setSearchResults([]);
    }
  };

  const onCloseSearchResults = () => {
    setQuery('');
    setSearchResults([]);
  };

  const addResearchInterest = (ri: ResearchInterest) => {
    setQuery('');
    setSearchResults([]);

    if (selectedResearchInterests.length < maxResearchInterests) {
      onAdd(ri);
    }
  };

  return (
    <ResearchInterestTags
      query={query}
      onQueryInput={onQueryInput}
      isLoading={isLoading}
      selectedResearchInterests={selectedResearchInterests}
      searchResults={searchResults}
      onAdd={addResearchInterest}
      onRemove={onRemove}
      onCloseSearchResults={onCloseSearchResults}
      maxResearchInterests={maxResearchInterests}
      requiredText={requiredText}
      disabled={disabled}
      hasError={hasError}
    />
  );
}

interface UseResearchInterests {
  researchInterests: Array<ResearchInterest>;
  addResearchInterest(ri: ResearchInterest): void;
  removeResearchInterest(ri: ResearchInterest): void;
}

export function useResearchInterests(
  initialInterests: Array<ResearchInterest>,
): UseResearchInterests {
  const initialMap = OrderedMap(initialInterests.map((ri) => [ri.id, ri]));
  const [researchInterests, setResearchInterests] = useState(initialMap);

  const addResearchInterest = (ri: ResearchInterest) => {
    setResearchInterests(researchInterests.set(ri.id, ri));
  };

  const removeResearchInterest = (ri: ResearchInterest) => {
    setResearchInterests(researchInterests.delete(ri.id));
  };

  return {
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    researchInterests: Array.from(researchInterests.values()),
    addResearchInterest: addResearchInterest,
    removeResearchInterest: removeResearchInterest,
  };
}

function createDebouncedSearch(searchResearchInterests: SearchFunction) {
  return debounce(
    (
      query: string,
      risToExclude: Array<number>,
      onResults: (r: Array<ResearchInterest>) => void,
    ) => {
      searchResearchInterests(query, risToExclude).then(onResults);
    },
    500,
  );
}
